// classificationHelpers.ts
import { IField } from "types/stores/fieldTypes";
import { createDeepEqualSelector } from "../helpers/typedHelpers";
import fieldFollowsThirdPartyRules from "./fieldIsThirdParty";
import tryParseJSON from "./tryParseJSON";
import type { FlowFilter } from "../types/flowTypes";
import { FieldVisibilityTypes } from "./constants";
import { IAppState } from "../types/stores/index";

export const EXPERIAN_FOLDER_ID = "1";

export const get3PMShortNames = classificationTables => {
    if (!Array.isArray(classificationTables)) return [];
    return classificationTables.filter(fc => fc.ShortName === "3PM").map(fc => fc.ShortName);
};

// Determine category of a field
export const categoryOf = (item, thirdPartyMPs) => {
    if (!item) return "uncategorized";

    const isExperian = item.companyid === 0;

    const isMarketplace =
        (!item.isLeaf && item.IsThirdParty && item.companyid !== 0) ||
        (item.isLeaf &&
            item.FieldClassification != null &&
            thirdPartyMPs.includes(item.FieldClassification) &&
            item.companyid !== 0);

    if (isExperian) return "experian";
    if (isMarketplace) return "marketplace";

    // MyData = not Experian, not Marketplace, not third-party
    const isMyData =
        item.FieldClassification != null &&
        !thirdPartyMPs.includes(item.FieldClassification) &&
        !item.IsThirdParty &&
        item.companyid !== 0;

    if (isMyData) return "mydata";

    return "uncategorized";
};

// Parent-child alignment
export const alignedWithParent = (item, parent, thirdPartyMPs) => {
    if (!item || !parent) return true; // safe fallback

    const childCat = categoryOf(item, thirdPartyMPs);
    const parentCat = categoryOf(parent, thirdPartyMPs);

    return childCat === parentCat;
};

export const selectIsSearchActive = (state: IAppState): boolean =>
    !!(state.search.modalTreeSearch || state.search.treeSearch) && state.vars.booleanSearchResultsReceived === true;

/**
 * Sorts child IDs with priority order:
 * 1. Force Experian (ID "1") to top
 * 2. Sort by field.sort property
 * 3. Alphabetical tiebreaker by field.text
 */
export const sortChildIds = (
    childIds: Array<string | number>,
    fieldsById: { [workId: number]: IField }
): Array<string | number> =>
    childIds.sort((a, b) => {
        // Guard against undefined fields
        const fieldA = fieldsById[a];
        const fieldB = fieldsById[b];
        if (!fieldA && !fieldB) return 0;
        if (!fieldA) return 1;
        if (!fieldB) return -1;

        if (a == EXPERIAN_FOLDER_ID) {
            return -1;
        }
        if (b == EXPERIAN_FOLDER_ID) {
            return 1;
        } // Force experian to top
        if (fieldA.sort > fieldB.sort) {
            return 1;
        } // Sort by "Sort"
        if (fieldA.sort < fieldB.sort) {
            return -1;
        }

        const stringA: string | null = fieldA.text;
        const stringB: string | null = fieldB.text;

        if (!stringA || !stringB) {
            return 1;
        }
        return stringA.localeCompare(stringB); // Tiebreak by name
    });

export const makeGetLazyMisalignment = () =>
    createDeepEqualSelector(
        state => state.vars.lazyFieldsById, // ALL loaded fields so far (L1)
        state => state.vars.searchResults, // optional for lookup switching
        state => state.session.fieldClassificationTables,
        (lazyFieldsById, classTables) => {
            const items = lazyFieldsById || {};

            // FIX: Add defensive check for classTables
            const thirdPartyMPs = Array.isArray(classTables)
                ? classTables.filter(fc => fc.ShortName === "3PM").map(fc => fc.ShortName)
                : [];

            const misalignedMap: { [key: number]: boolean } = {};
            const orphanIds: number[] = [];

            const byId = items;

            // Build quick map parent -> child IDs
            const byParent = {};

            Object.values(byId).forEach(_item => {
                const item = _item as IField;

                if (!item || item.parent == null) return;

                if (!byParent[item.parent]) byParent[item.parent] = [];
                byParent[item.parent].push(item.workid!);
            });

            // Detect misaligned & orphan
            Object.values(byId).forEach(_item => {
                const item = _item as IField;
                if (!item || item.workid == null) return;

                const parentId = item.parent;
                const parent = byId[parentId];

                const isAligned = alignedWithParent(item, parent, thirdPartyMPs);

                if (!isAligned) {
                    misalignedMap[item.workid] = true;
                    orphanIds.push(item.workid);
                }
            });

            return {
                misalignedMap,
                orphanIds, // these are global orphans discovered so far
            };
        }
    );

export const makeLazyTabFilterSelector = () =>
    createDeepEqualSelector(
        (_, props) => props.tabType,
        (_, props) => props.showTabs,
        (_, props) => props.lookup, // lookup table (byFolder or searchResults)
        (_, props) => props.childIds, // sorted child IDs
        (_, props) => props.selectedFieldId, // parent folder ID
        state => state.session.fieldClassificationTables,
        state => state.vars.lazyFieldsById, // ALL loaded fields
        (tabType, showTabs, lookup, childIds, selectedFieldId, classTables, lazyFieldsById) => {
            if (!showTabs) {
                return {
                    folderIds: childIds.filter(id => !lookup[id]?.isLeaf),
                    fieldIds: childIds.filter(id => lookup[id]?.isLeaf),
                };
            }

            // FIX: Add defensive check for classTables
            const thirdPartyMPs = Array.isArray(classTables)
                ? classTables.filter(fc => fc.ShortName === "3PM").map(fc => fc.ShortName)
                : [];

            const catOf = id => categoryOf(lookup[id], thirdPartyMPs);

            // Helper functions matching FieldList.tsx
            const isUncategorized = (item: IField) => {
                if (!item) return false;
                const isExperian = item.companyid === 0;
                const isMarketplaceFolder = !item.isLeaf && item.IsThirdParty && item.companyid !== 0;
                const isMarketplaceField =
                    item.isLeaf &&
                    item.FieldClassification !== null &&
                    thirdPartyMPs.includes(item.FieldClassification) &&
                    item.companyid !== 0;
                const isMyData =
                    item.FieldClassification !== null &&
                    !thirdPartyMPs.includes(item.FieldClassification) &&
                    !item.IsThirdParty &&
                    item.companyid !== 0;

                return !isExperian && !isMarketplaceFolder && !isMarketplaceField && !isMyData;
            };

            // ================ TAB LOGIC ================
            let filtered = [...childIds];

            if (tabType === 0) {
                // Experian
                filtered = filtered.filter(id => catOf(id) === "experian");
            } else if (tabType === 1) {
                // Marketplace
                filtered = filtered.filter(id => catOf(id) === "marketplace");
            } else if (tabType === 2) {
                // MyData
                filtered = filtered.filter(id => catOf(id) === "mydata");
            } else if (tabType === 3) {
                // OTHER: Misconfigured fields and folders for Admin > Edit Fields
                const atTopOfFieldTree = !selectedFieldId || selectedFieldId == "#";

                // Get ALL item IDs from lazyFieldsById (global scope)
                const allItemIds = Object.keys(lazyFieldsById || {}).map(Number);

                // Find all orphaned items globally using lazyFieldsById
                const allOrphanedItemIds = allItemIds.filter(id => {
                    const item = lazyFieldsById[id];
                    const parent = lazyFieldsById[item?.parent];
                    return !alignedWithParent(item, parent, thirdPartyMPs);
                });

                // Check if the selected folder itself is an orphan
                const selectedItemIsAnOrphan = allOrphanedItemIds.includes(selectedFieldId as number);

                // Start with uncategorized items from current folder
                let filteredIds = childIds.filter(id => isUncategorized(lookup[id]));

                if (atTopOfFieldTree) {
                    // At root: show uncategorized + all global orphans
                    filteredIds.push(...allOrphanedItemIds);
                } else if (selectedItemIsAnOrphan) {
                    // Inside an orphan folder: show only properly aligned children
                    filteredIds = childIds.filter(id => {
                        const item = lookup[id];
                        const parent = lookup[selectedFieldId];
                        return alignedWithParent(item, parent, thirdPartyMPs);
                    });
                }

                // Remove duplicates
                filteredIds = [...new Set(filteredIds)];

                filtered = filteredIds;
            }

            return {
                folderIds: filtered.filter(id => lookup[id] && !lookup[id].isLeaf),
                fieldIds: filtered.filter(id => lookup[id] && lookup[id].isLeaf),
            };
        }
    );

/**
 * Calculates which fields should be selected in a Ctrl+Shift range selection
 */
export const getCtrlShiftSelectionRange = (
    lastSelected: number,
    newSelected: number,
    fieldIds: Array<string | number>,
    lookup: { [key: number]: any },
    currentlySelectedFields: Array<number>
): Array<number> => {
    const fieldsByFolder = Object.values(lookup);
    const fieldList = fieldIds.map(x => {
        const field = fieldsByFolder.find(y => y.workid! == x);
        return field ? field.fieldkey : null;
    });

    const indexA = fieldList.indexOf(lastSelected);
    const indexB = fieldList.indexOf(newSelected);

    const fieldsToAdd: Array<number> = [];

    if (indexA > indexB) {
        for (let i = indexB; i <= indexA; i++) {
            let field = fieldList[i];
            if (field && !currentlySelectedFields.includes(field)) {
                fieldsToAdd.push(field);
            }
        }
    } else if (indexB > indexA) {
        for (let i = indexA; i <= indexB; i++) {
            let field = fieldList[i];
            if (field && !currentlySelectedFields.includes(field)) {
                fieldsToAdd.push(field);
            }
        }
    }

    return fieldsToAdd;
};

/**
 * Determines if a field should be grayed out based on flow filter rules.
 * This version is optimized for lazy-loaded field trees.
 *
 * @param field - The field to check for grayout
 * @param lookup - Field lookup dictionary (only contains loaded fields)
 * @param flowFiltersById - All flow filters indexed by ID
 * @param flowFiltersInFlow - Filters active in the current flow
 * @param thirdPartyShortNames - Array of third-party classification short names
 * @param editable - Whether the tree is in edit mode
 * @returns true if the field should be grayed out
 */
export const shouldGrayoutField = (
    field: IField,
    lookup: { [key: number | string]: IField },
    flowFiltersById: { [key: string]: FlowFilter },
    flowFiltersInFlow: Array<any>,
    thirdPartyShortNames: Array<string>,
    editable: boolean
): boolean => {
    // Only apply grayout logic in non-editable mode for third-party fields
    if (!fieldFollowsThirdPartyRules(field.FieldClassification, field.IsThirdParty, thirdPartyShortNames) || editable) {
        return false;
    }

    const theseFilters = Object.values<FlowFilter>(flowFiltersById);

    for (const theseFilter of theseFilters) {
        // Check if this filter is active in the current flow
        if (flowFiltersInFlow.filter(x => x.FlowItemId == theseFilter.FlowItemId).length > 0) {
            const includeObject = tryParseJSON(theseFilter.FlowFilterCriteria) || {};

            if (includeObject.rules) {
                for (const rule of includeObject.rules) {
                    // IMPORTANT: Only check if the filter field is loaded in lookup
                    const filterField = lookup[rule.id];

                    // Skip if filter field not loaded yet (lazy loading)
                    if (!filterField) {
                        continue;
                    }

                    // Check if filter field is third-party and from different company
                    if (
                        fieldFollowsThirdPartyRules(
                            filterField?.FieldClassification,
                            filterField?.IsThirdParty,
                            thirdPartyShortNames
                        ) &&
                        field.companyid !== filterField.companyid
                    ) {
                        return true; // Grayout this field
                    }
                }
            }
        }
    }

    return false;
};

/**
 * Applies field visibility filtering for edit mode (lazy-loading aware).
 * Filters fields based on admin-selected visibility types (Export, Report, None).
 */
export const applyFieldVisibilityFilter = (
    fieldIds: Array<string | number>,
    lookup: { [key: number | string]: IField },
    fieldVisibilityFilter: Array<number>,
    enabledFieldVisibilityTypes: { [fieldId: number]: Array<number> }
): Array<string | number> =>
    fieldIds.filter(x => {
        const field = lookup[x];

        // CRITICAL: Skip if field not loaded yet (lazy loading)
        if (!field || !field.fieldkey) {
            return false; // Don't show fields that aren't loaded
        }

        // Now apply visibility filtering to loaded fields
        return (
            // Option 1: "None" selected and field has no visibility types
            (fieldVisibilityFilter.includes(FieldVisibilityTypes.NONE) &&
                !enabledFieldVisibilityTypes[field.fieldkey]) ||
            // Option 2: Field has visibility types matching the filter
            (enabledFieldVisibilityTypes[field.fieldkey] &&
                fieldVisibilityFilter.some(f => enabledFieldVisibilityTypes[field.fieldkey].includes(f)))
        );
    });

/**
 * Filter field IDs by selected field labels
 */
export const applyFieldLabelFilter = (
    fieldIds: Array<string | number>,
    fieldsById: { [workId: number]: IField },
    selectedFieldLabels: Array<string>
): Array<string | number> =>
    fieldIds.filter(x => {
        const field = fieldsById[x];
        if (!field) return false;

        const fieldIdLabels = field.FieldLabels && field.FieldLabels.split(", ");
        return fieldIdLabels ? fieldIdLabels.some(label => selectedFieldLabels.includes(label)) : false;
    });

/**
 * Filter field IDs by selected field labels3 (UmbrellaFieldId)
 */
export const applyFieldLabels3Filter = (
    fieldIds: Array<string | number>,
    fieldsById: { [workId: number]: IField },
    selectedFieldLabels3: Array<string>
): Array<string | number> =>
    fieldIds.filter(x => {
        const field = fieldsById[x];
        if (!field) return false;

        const fieldIdLabels3 = field.UmbrellaFieldId != null ? field.UmbrellaFieldId : "";
        return fieldIdLabels3 ? fieldIdLabels3 == Number(selectedFieldLabels3) : false;
    });
